import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, MapPin, ShoppingCart, LogIn, LogOut, User } from 'lucide-react';
import { Button } from './ui/button';
import CartDrawer from './CartDrawer';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  const { getCartCount } = useCart();
  const { user, isAuthenticated, isAdmin, logout } = useAuth();

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/prodotti';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleLogout = async () => {
    try {
      await logout();
      window.location.href = '/';
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Chi Siamo', path: '/chi-siamo' },
    { name: 'Prodotti', path: '/prodotti' },
    { name: 'Servizi', path: '/servizi' },
    { name: 'Marchi', path: '/marchi' },
    { name: 'Contatti', path: '/contatti' }
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      isScrolled ? 'bg-white shadow-md' : 'bg-white/95 backdrop-blur-sm'
    }`}>
      {/* Top bar */}
      <div className="bg-gradient-to-r from-slate-700 to-slate-600 text-white py-2">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap items-center justify-between text-sm">
            <div className="flex items-center gap-4">
              <a href="tel:0883620626" className="flex items-center gap-2 hover:text-blue-400 transition-colors">
                <Phone size={14} />
                <span>0883 620626</span>
              </a>
              <div className="hidden sm:flex items-center gap-2 text-slate-300">
                <MapPin size={14} />
                <span>San Ferdinando di Puglia (BT)</span>
              </div>
            </div>
            <div className="text-slate-300">
              Lun-Ven: 07:00-12:30 / 14:00-17:30
            </div>
          </div>
        </div>
      </div>

      {/* Main navigation */}
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group py-1">
            <img 
              src="https://customer-assets.emergentagent.com/job_edil-service/artifacts/nsw2ji7s_IMG_1877.png" 
              alt="Edil Service 2.0" 
              className="h-16 w-auto transition-transform group-hover:scale-105"
            />
            <span className="text-slate-800 font-bold text-lg hidden md:block" style={{ fontFamily: "Arial, sans-serif" }} translate="no">
              Edil Service 2.0 SRL
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`px-4 py-2 rounded-lg font-medium transition-all ${
                  isActive(link.path)
                    ? 'text-blue-600 bg-blue-50'
                    : 'text-slate-700 hover:text-blue-600 hover:bg-slate-50'
                }`}
              >
                {link.name}
              </Link>
            ))}
          </nav>

          {/* CTA Button */}
          <div className="hidden lg:flex items-center gap-3">
            {isAuthenticated && !isAdmin ? (
              <>
                <div className="flex items-center gap-2 px-3 py-1.5 bg-slate-50 rounded-lg">
                  {user?.picture && (
                    <img src={user.picture} alt={user.name} className="w-6 h-6 rounded-full" />
                  )}
                  <span className="text-sm font-medium text-slate-700">{user?.name}</span>
                </div>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={handleLogout}
                  className="text-slate-600 hover:text-slate-800"
                >
                  <LogOut size={16} className="mr-2" />
                  Esci
                </Button>
              </>
            ) : !isAdmin && location.pathname !== '/admin/login' && (
              <Button 
                onClick={handleGoogleLogin}
                variant="outline"
                className="text-blue-600 border-blue-600 hover:bg-blue-50"
              >
                <LogIn size={16} className="mr-2" />
                Accedi
              </Button>
            )}
            <CartDrawer>
              <Button variant="outline" className="relative">
                <ShoppingCart size={20} />
                {getCartCount() > 0 && (
                  <span className="absolute -top-2 -right-2 bg-blue-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold">
                    {getCartCount()}
                  </span>
                )}
              </Button>
            </CartDrawer>
            <Link to="/contatti">
              <Button className="bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-700 hover:to-blue-600 text-white shadow-lg shadow-blue-500/30">
                Richiedi Preventivo
              </Button>
            </Link>
          </div>

          {/* Mobile Cart Button */}
          <div className="lg:hidden flex items-center gap-2">
            <CartDrawer>
              <Button variant="outline" size="sm" className="relative">
                <ShoppingCart size={18} />
                {getCartCount() > 0 && (
                  <span className="absolute -top-1 -right-1 bg-orange-600 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center font-bold text-[10px]">
                    {getCartCount()}
                  </span>
                )}
              </Button>
            </CartDrawer>

            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="p-2 text-slate-700 hover:text-blue-600 transition-colors"
              aria-label="Toggle menu"
            >
              {isMenuOpen ? <X size={28} /> : <Menu size={28} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      {isMenuOpen && (
        <div className="lg:hidden bg-white border-t border-slate-200 shadow-lg">
          <nav className="container mx-auto px-4 py-4 flex flex-col gap-2">
            {isAuthenticated && !isAdmin && (
              <div className="flex items-center gap-2 px-4 py-3 bg-slate-50 rounded-lg mb-2">
                {user?.picture && (
                  <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full" />
                )}
                <div className="flex-1">
                  <p className="text-sm font-medium text-slate-700">{user?.name}</p>
                  <p className="text-xs text-slate-500">{user?.email}</p>
                </div>
              </div>
            )}
            
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setIsMenuOpen(false)}
                className={`px-4 py-3 rounded-lg font-medium transition-all ${
                  isActive(link.path)
                    ? 'text-blue-600 bg-blue-50'
                    : 'text-slate-700 hover:text-blue-600 hover:bg-slate-50'
                }`}
              >
                {link.name}
              </Link>
            ))}
            
            {isAuthenticated && !isAdmin ? (
              <Button 
                onClick={() => { handleLogout(); setIsMenuOpen(false); }}
                variant="outline"
                className="w-full mt-2 text-slate-600 hover:text-slate-800"
              >
                <LogOut size={16} className="mr-2" />
                Esci
              </Button>
            ) : !isAdmin && (
              <Button 
                onClick={() => { handleGoogleLogin(); setIsMenuOpen(false); }}
                variant="outline"
                className="w-full mt-2 text-blue-600 border-blue-600 hover:bg-blue-50"
              >
                <LogIn size={16} className="mr-2" />
                Accedi con Google
              </Button>
            )}
            
            <Link to="/contatti" onClick={() => setIsMenuOpen(false)}>
              <Button className="w-full mt-2 bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-700 hover:to-blue-600 text-white">
                Richiedi Preventivo
              </Button>
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
